import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';

export interface PublicAuthConfig {
  methods: {
    emailPassword: { enabled: boolean };
    google: { enabled: boolean };
    meta: { enabled: boolean };
    phoneOtp: { enabled: boolean };
    magicLink: { enabled: boolean };
  };
  signup: {
    enabled: boolean;
    requireEmailVerification: boolean;
  };
  passwordPolicy: {
    minLength: number;
    maxLength: number;
    requireUppercase: boolean;
    requireDigit: boolean;
    requireSpecialChar: boolean;
  };
  signupFields: Array<{
    field: string;
    label: string;
    required: boolean;
    type: 'text' | 'date' | 'select' | 'phone' | 'email';
    options?: string[];
    placeholder?: string;
  }>;
  consentCheckboxes: Array<{
    id: string;
    label: string;
    linkUrl?: string;
    linkText?: string;
    required: boolean;
    consentChannel?: string;
  }>;
}

/** Default config used as fallback when the API is unavailable (graceful degradation). */
const DEFAULT_CONFIG: PublicAuthConfig = {
  methods: {
    emailPassword: { enabled: true },
    google: { enabled: true },
    meta: { enabled: true },
    phoneOtp: { enabled: true },
    magicLink: { enabled: false },
  },
  signup: { enabled: true, requireEmailVerification: false },
  passwordPolicy: {
    minLength: 8,
    maxLength: 128,
    requireUppercase: false,
    requireDigit: false,
    requireSpecialChar: false,
  },
  signupFields: [],
  consentCheckboxes: [],
};

interface AuthConfigState {
  data: PublicAuthConfig | undefined;
  isLoading: boolean;
  error: unknown;
}

// Module-level cache so the fetch is shared across components
let cachedConfig: PublicAuthConfig | undefined;
let cacheTimestamp = 0;
const CACHE_TTL = 60_000; // 1 minute

export function useAuthConfig(): AuthConfigState {
  const [state, setState] = useState<AuthConfigState>({
    data: cachedConfig,
    isLoading: !cachedConfig,
    error: undefined,
  });
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Already have fresh cached data
    if (cachedConfig && Date.now() - cacheTimestamp < CACHE_TTL) {
      setState({ data: cachedConfig, isLoading: false, error: undefined });
      return;
    }

    if (fetchedRef.current) return;
    fetchedRef.current = true;

    let cancelled = false;

    api
      .get<PublicAuthConfig>('/customer/auth/config')
      .then((res) => {
        if (cancelled) return;
        cachedConfig = res.data;
        cacheTimestamp = Date.now();
        setState({ data: res.data, isLoading: false, error: undefined });
      })
      .catch((err) => {
        if (cancelled) return;
        // Graceful degradation: use defaults on error
        setState({ data: DEFAULT_CONFIG, isLoading: false, error: err });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
